import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient, setActiveWorkspaceId } from '../api/client';
import { ME_QUERY_KEY, meQueryOptions } from '../lib/meQuery';
import { useAccessToken } from '../auth/useAuth';
import { AI_CONSENT_VERSION, AI_FIRM_CONSENT_VERSION, type AiConsentScope, type MeResponse, type WorkspaceMembership, type PendingInvite, type UserProfile, type UserPreferences } from '../api/types';
import { CREDENTIAL_CONSENT_VERSION } from '@steward-app/steward-types';
import { recordTermsAcceptance } from '../terms/recordTermsAcceptance';
import { BrandSync } from '../brand/BrandProvider';
import { ADVISOR_FIRMS_QUERY_KEY } from '../advisor/useAdvisorFirm';
import { ADVISOR_CLIENTS_QUERY_KEY } from '../advisor/useAdvisorClients';
import { ADVISOR_SUMMARY_QUERY_KEY } from '../advisor/useAdvisorSummary';
import { LINKED_ADVISOR_OFFER_QUERY_KEY } from '../advisor/useLinkedAdvisorOffer';
import { readStored, writeStored } from './workspaceStorage';

/**
 * Persist the workspace a subsequent WorkspaceProvider mount should resolve to
 * as active, WITHOUT going through `useWorkspace()`. First-run onboarding
 * (W1.11) completes its "create your space" step from ABOVE
 * WorkspaceProvider (identity-scoped gates run before it, same reasoning as
 * EnrollmentGate) — it has no provider instance to call `switchWorkspace` on
 * yet. Writing the same storage key WorkspaceProvider itself reads on mount
 * (`resolveActive`) means the very first `/me` fetch already lands on the
 * freshly created workspace instead of the façade's arbitrary fallback.
 *
 * `remember: 'both'` (the default): a freshly created workspace is a durable home,
 * not a tab-only look-in.
 */
export function persistActiveWorkspaceId(id: string): void {
  writeStored(id);
}

/** A workspace the user can actually enter: their membership is active AND the
 *  workspace itself is active. `workspaceStatus` is absent for never-suspended
 *  workspaces (the fan-out only writes it on a status change) ⇒ treat absent as
 *  active, matching the appcore middleware. */
export function isUsableMembership(m: WorkspaceMembership): boolean {
  return m.status === 'active' && (!m.workspaceStatus || m.workspaceStatus === 'active');
}

/** An active membership whose workspace is *not* active — the user still belongs to
 *  it but can't enter it (suspended for billing / trial-ended, provisioning, …).
 *  Surfaced as a "paused" state instead of silently dropping the user into a
 *  workspace that 403s on every call (O4 suspended-state). */
export function isSuspendedMembership(m: WorkspaceMembership): boolean {
  return m.status === 'active' && !!m.workspaceStatus && m.workspaceStatus !== 'active';
}

/**
 * Paths a user with no usable workspace is allowed to sit on, so the member-less
 * redirect below doesn't bounce them off pages that never needed a workspace.
 * Mirrors AssuranceFloorGate's WORKSPACE_PICKER_PATH exemption, widened to the
 * identity-scoped half of Settings.
 *
 * WHY THESE, AND WHY A LIST RATHER THAN A `/settings/` PREFIX. Settings is split:
 * Profile / Security / Preferences read and write the USER (`GET /me`, `PUT /me`,
 * `/me/logins` — all identity-scoped, all fine with `activeMembership === null`,
 * which Settings.tsx already tolerates by hiding the admin tabs). Members,
 * Workspace and Connections are workspace-scoped — Connections renders
 * ConnectedAccounts, whose Plaid items belong to a workspace — so a prefix match
 * would hand a member-less user three pages that can only 403.
 *
 * `/settings` and `/settings/logins` are here because they are the URLs a stuck
 * user actually types or follows: the index redirects to Profile and `/logins` is
 * a LegacyRedirect to Security (docs/29 F4), but this redirect fires on the path
 * BEFORE that hop, so leaving them out would bounce the deep link and leave only
 * the destination reachable. The first-run `account-exists` message points here,
 * which is the whole reason this matters: telling a locked-out user to visit
 * Settings and then bouncing them to the picker makes a correct refusal look broken.
 */
const MEMBERLESS_ALLOWED_PATHS: readonly string[] = [
  '/select-workspace',
  '/settings',
  '/settings/profile',
  '/settings/sign-in-keys',
  '/settings/logins',
  '/settings/preferences',
  // The onboarding form and requests (steward-app#2465). Identity-scoped like Profile: the screens
  // read `/me` and the caller's own `/me/onboarding-forms/{firmWorkspaceId}` routes, which carry no
  // workspace. A person whose only workspace is a pending invite, or whose workspaces are all
  // suspended, can still be invited by a firm and must be able to answer. The form's reads and
  // writes keep their own server-side checks.
  '/onboarding',
  '/onboarding/form',
  '/onboarding/done',
  '/onboarding/requests',
];

/** True when `pathname` is reachable without a usable workspace. Exact match on a
 *  trailing-slash-normalized path — deliberately not a prefix test (see above). */
export function isMemberlessAllowedPath(pathname: string): boolean {
  const normalized = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  return MEMBERLESS_ALLOWED_PATHS.includes(normalized);
}

/** Default resolution (doc §6): last-used id if it's still a *usable* membership,
 *  else the first usable workspace by name. Null when the user has no usable
 *  workspace (member-less, or every workspace suspended). */
export function resolveActive(stored: string | null, memberships: WorkspaceMembership[]): string | null {
  const usable = memberships.filter(isUsableMembership);
  if (stored && usable.some(m => m.workspaceId === stored)) return stored;
  const firstByName = [...usable].sort((a, b) => a.workspaceName.localeCompare(b.workspaceName))[0];
  return firstByName?.workspaceId ?? null;
}

/** What an accept may carry. Only a SPONSORED invitation's card sets it (steward-app#1488). */
export interface AcceptInviteOptions {
  /** The sponsored acceptance disclosure copy version the card rendered. */
  disclosureVersion?: string;
  /**
   * The areas the card's sentences named, for a disclosure that names areas (steward-app#1971):
   * what the firm starts with, and what stays on. Sent only together, and only with a version.
   */
  startAreas?: readonly string[];
  lockedAreas?: readonly string[];
}

interface WorkspaceContextValue {
  memberships: WorkspaceMembership[];
  /** True once GET /me has settled (success OR error). Until then `memberships`
   *  is transiently empty — surfaces that branch on "member-less" (the founder
   *  arrival screen, the picker's empty state) must wait for this or they flash
   *  the wrong face at every user while /me loads. */
  meLoaded: boolean;
  /**
   * GET /me actually ANSWERED, i.e. `memberships` and `invites` are the server's lists
   * rather than this provider's `[]` placeholder (steward-app#1312).
   *
   * ⚠️ **Read this, not {@link meLoaded}, before asserting that one of those lists is
   * EMPTY.** `meLoaded` is true for a `/me` that settled with an ERROR — it answers "stop
   * showing the loading face", which is all its consumers ever needed — and both lists are
   * `data?.x ?? []`, so an errored `/me` presents as "settled, and you have none". A surface
   * that says "nothing is waiting for you" off that is making a confident claim about a
   * request that never answered.
   *
   * Stays true when a REFETCH fails over a cached payload: those lists are stale but real.
   */
  meSucceeded: boolean;
  /** The signed-in user's identity (from GET /me) — drives the greeting, account
   *  menu, and Settings → Profile. Null until /me resolves. */
  profile: UserProfile | null;
  /** Persist a change to what the app calls the user (PUT /me), then refresh /me so
   *  the greeting + account menu update everywhere. */
  updateProfile: (input: { name?: string; givenName?: string }) => Promise<void>;
  /** Persist the getting-started checklist dismissal for a workspace (FR2), optimistically
   *  and cross-device. `dismissed=false` re-shows it (used by the Preferences toggle). */
  setFirstRunDismissed: (workspaceId: string, dismissed: boolean) => Promise<void>;
  /** Persist the AI assistant consent acknowledgment (docs/15-ai-consent-privacy.md),
   *  optimistically and cross-device — same pattern as {@link setFirstRunDismissed}. */
  /** Records the acknowledgment for one dialog's pair; the other pair is left untouched. */
  acknowledgeAiConsent: (scope: AiConsentScope) => Promise<void>;
  /** Record agreement to the credential vault's terms (steward-app#2011). NOT optimistic and
   *  NOT a preferences patch, unlike its neighbours: the façade stamps the time itself and
   *  refuses these keys from `setPreferences`, so the cache is only moved by the refetch
   *  that follows a successful write. Rejects if the write fails, so the caller can say so. */
  acceptCredentialConsent: () => Promise<void>;
  /** Record acceptance of the Steward Terms and Privacy Policy (steward-app#2630), then refetch
   *  `/me`. Same shape as {@link acceptCredentialConsent}: not optimistic, and rejects if the
   *  write fails. Call it only from a click with `TermsAcceptanceLine` beside the control. */
  acceptTerms: () => Promise<void>;
  /** Persist a patch to the per-user preferences blob (PUT /me), optimistically and
   *  cross-device — the generic form of {@link setFirstRunDismissed}. Appcore
   *  shallow-merges, so unrelated keys survive. */
  setPreferences: (patch: Partial<UserPreferences>) => Promise<void>;
  /** Pending workspace invitations for the signed-in user (from GET /me). */
  invites: PendingInvite[];
  /**
   * The user can still create their OWN organization — they hold an unconsumed
   * eligibility grant (appcore#346). Do NOT re-derive this from `memberships`:
   * belonging to someone else's workspace says nothing about whether your own
   * grant is spent, and inferring it from an empty membership list is what used
   * to strand a founder who accepted an invitation first (#430). Falls back to
   * that inference only while appcore has not yet shipped the field.
   */
  canBootstrapTenant: boolean;
  activeWorkspaceId: string | null;
  activeMembership: WorkspaceMembership | null;
  /**
   * True while a `switchWorkspace(...)` call's own navigation hasn't reached its
   * destination yet — see `pendingSwitchTarget`'s comment on the implementation for the
   * full mechanism (steward-app#1409) and `RequireAdvisorFirm`'s self-heal for the one
   * consumer today.
   *
   * **Guarantees exactly one thing: `activeWorkspaceId` may already be the switch's
   * target while `useLocation()` is not yet.** It says nothing about whether the switch
   * will succeed, how long it will take, or what page it lands on — a caller reading
   * `switchInFlight` to gate anything other than "hold a route decision that keys on
   * `activeWorkspaceId` until the URL agrees with it" is answering a question this field
   * was not built to answer.
   */
  switchInFlight: boolean;
  /** Switch the active workspace (must be one the user belongs to), drop cached
   *  scoped data so it refetches under the new workspace, and navigate.
   *  Without `destination`, stays on the current path (dropping a trailing
   *  detail-record id, since that specific record doesn't exist in the new
   *  workspace) — the right default for a picker the user is already looking
   *  at a list from. Pass `destination` when the caller means "take me INTO
   *  that workspace" from somewhere that isn't itself a page of the new
   *  workspace's content (the advisor portal's "Open", docs/25 §2/S7) — it
   *  avoids the two-transition dance of switching in place and then
   *  navigating again.
   *
   *  `opts.replace` (default `true`) and `opts.remember` (default `'both'`) both
   *  default to the pre-steward-app#1401 behaviour, so every caller but
   *  `AdvisorClients.tsx`'s `openClient` is unchanged — see the implementation's own
   *  doc on each for what the non-default value is for. */
  switchWorkspace: (
    workspaceId: string,
    destination?: string,
    opts?: { replace?: boolean; remember?: 'both' | 'tab' },
  ) => void;
  /** Rename a workspace (WL3, owner-only — appcore enforces). Refreshes /me so the
   *  switcher reflects the new name. */
  renameWorkspace: (workspaceId: string, name: string) => Promise<void>;
  /** Soft-delete a workspace (WL3, owner-only — appcore enforces + blocks the last
   *  one). If it was the active workspace, falls back to another membership (or
   *  /select-workspace) before /me catches up with the async cascade.
   *
   *  Has no SPA caller while steward-app#1747 hides the "Delete workspace" control
   *  (`WorkspaceSettings`'s own header) for every user; kept rather than deleted,
   *  since #1748/#1749 bring it back. */
  deleteWorkspace: (workspaceId: string) => Promise<void>;
  /** Hide a workspace from `memberships` right away, for a caller whose own write
   *  (through some OTHER route than {@link deleteWorkspace}) already removed this user's
   *  membership in it — a sponsored setup's Cancel or End (`SponsoredClientActions.tsx`).
   *  Same immediate-hide `deleteWorkspace` gives itself while the async membership-mirror
   *  cascade catches up; never issues a write of its own. */
  noteWorkspaceRemoved: (workspaceId: string) => void;
  /** Accept a pending invite by token (FOP4) — joins the workspace, refreshes /me,
   *  and switches into the newly-joined workspace. Throws ApiError on 403/409. */
  acceptInvite: (token: string, options?: AcceptInviteOptions) => Promise<void>;
  /** Accept a pending invite by token WITHOUT switching workspace and without
   *  navigating (steward-app#1312) — joins the workspace and refreshes /me, leaving the
   *  caller exactly where it was. For a surface that admits someone else's workspace as
   *  part of its own work (the advisor portal's "Waiting for you") rather than to go and
   *  look at it. Throws ApiError on 403/409, same as {@link acceptInvite}. */
  acceptInviteInPlace: (token: string) => Promise<void>;
  /** Open the Stripe Billing Portal for a (typically suspended) workspace so a
   *  tenant-admin can update payment / reactivate (O7). Redirects the browser to the
   *  Stripe-hosted session on success. Throws ApiError (409 = no billing account). */
  manageBilling: (workspaceId: string, returnPath?: string) => Promise<void>;
  /** Re-fetch GET /me (e.g. after creating a workspace). Resolves with what THAT read returned:
   *  the `/me` payload, or `undefined` when the read failed (it never rejects, and a failed read
   *  leaves the previous payload in place, so the provider's lists alone cannot tell the two
   *  apart). Most callers ignore it. */
  refresh: () => Promise<MeResponse | undefined>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within a WorkspaceProvider');
  return ctx;
}

/**
 * Loads the user's workspaces (GET /me) and owns the active-workspace selection
 * for the whole authenticated app (O3). It keeps the api-client's ambient
 * `X-Workspace-Id` in sync so every scoped request targets the selected workspace,
 * gates the UI until /me resolves, and routes a member-less user to /select-workspace.
 */
export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Latest path, read at switch time without making switchWorkspace re-bind every
  // navigation (which would churn the context value).
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  // Loaded in the background — the app shell is NOT gated on it. While /me is
  // pending or if it fails (e.g. a session whose platform user/membership isn't
  // provisioned), `memberships` is empty, the switcher hides, and scoped requests
  // fall back to the façade's default workspace (pre-O3 behavior). This keeps the
  // portal usable instead of dead-ending on a full-screen error.
  const { data, refetch, isError } = useQuery(meQueryOptions(token));

  // Workspaces deleted this session. The WL2 cascade (membership-mirror
  // deactivation) is Streams-driven and async, so a fresh /me can still list a
  // just-deleted workspace as active — we hide it locally until /me catches up so
  // it can't reappear in the switcher or be re-resolved as active.
  const [locallyDeleted, setLocallyDeleted] = useState<string[]>([]);
  const memberships = useMemo(
    () => (data?.memberships ?? []).filter(m => !locallyDeleted.includes(m.workspaceId)),
    [data, locallyDeleted],
  );
  // The user's explicit pick (or last-used from storage).
  const [selectedId, setSelectedId] = useState<string | null>(() => readStored());
  // ⚠️ **The destination an in-flight `switchWorkspace(...)` call asked `navigate` for,
  // until the URL actually gets there — steward-app#1409.** Set synchronously inside
  // `switchWorkspace`, in the SAME callback as `setSelectedId` above, so it is already
  // current on the very next render that reflects the new `activeWorkspaceId`.
  //
  // **Why this exists at all: `activeWorkspaceId` and `useLocation()` do NOT always
  // update in the same commit, and #1409 is what that costs.** `setSelectedId` is an
  // ordinary, synchronous `setState`; `navigate()` (react-router 7, confirmed against
  // `node_modules/react-router/dist/development/chunk-HHGH3NKS.js` — both the plain
  // `<Router>` and the data router's `RouterProvider` wrap their own location-state
  // update in `React.startTransition`) is not. React gives the synchronous update
  // priority, so a `switchWorkspace(clientId, '/', ...)` call commits at least one real,
  // painted render where `activeWorkspaceId` already reads the new workspace while
  // `useLocation().pathname` still reads the OLD route — reproduced directly in
  // `openClient.regression.test.tsx` by logging both on every render. `RequireAdvisorFirm`
  // mounts the WHOLE `/advisor` subtree, so it is still on screen for that one commit,
  // and its steward-app#1401 self-heal (`inClientFrame` now true, route still
  // `/advisor/clients`) fired and switched straight back to the firm — before the
  // `navigate('/')` this same click asked for ever took visible effect. The click read as
  // inert because it was immediately, correctly-looking undone.
  //
  // `switchInFlight` (derived below, exposed on the context) is what tells THAT commit
  // apart from a genuinely stale route (browser back, a bookmark, a typed URL) reached
  // with NO switch in flight at all: only the former has a `pendingSwitchTarget` that
  // disagrees with the current location. It is not itself the fix — see
  // `RequireAdvisorFirm`'s own effect for the one consumer — only the settled signal that
  // fix is driven from, rather than the transitional `inClientFrame` alone.
  const [pendingSwitchTarget, setPendingSwitchTarget] = useState<string | null>(null);
  // The effective active workspace. Until /me loads we can't validate the stored
  // selection against memberships, so trust it **optimistically** — this keeps a
  // reload on a non-default workspace fetching THAT workspace's data instead of the
  // façade's fallback (first) workspace. Once /me loads we resolve properly (a
  // stale/forged pick falls back to a valid one) and the effect below refetches.
  const activeWorkspaceId = useMemo(
    () => (data ? resolveActive(selectedId, memberships) : selectedId),
    [data, selectedId, memberships],
  );

  // Sync the api-client's ambient workspace synchronously (before children's
  // queries fire) so scoped requests always carry the resolved workspace.
  setActiveWorkspaceId(activeWorkspaceId);

  // True on exactly the transitional commit(s) `pendingSwitchTarget`'s own comment
  // describes: a switch asked to land on a path the URL hasn't reached yet. Derived
  // fresh on every render (not read from a ref) so it agrees with THIS render's own
  // `location.pathname` the instant the router's deferred update finally lands, with no
  // extra render needed to notice. `location.pathname === pendingSwitchTarget` (a switch
  // that never leaves the current page, e.g. `WorkspaceSwitcher.tsx` picking a firm while
  // already on `/advisor/clients`) is `false` here immediately, on the very first render —
  // there is no transitional window to name for a switch that isn't asking the URL to move.
  const switchInFlight = pendingSwitchTarget !== null && pendingSwitchTarget !== location.pathname;

  // Clears `pendingSwitchTarget` once the URL genuinely reflects it — the render after the
  // transition-wrapped location update above finally commits, whatever else changed in the
  // meantime. Passive (not layout): nothing here is paint-timing sensitive, unlike the
  // store-sync effect below. Deliberately does NOT also fire on `activeWorkspaceId` or any
  // other dep — a switch is "in flight" purely as a function of where the URL has and
  // hasn't gotten to, and adding another dependency would clear it on the wrong signal.
  useEffect(() => {
    if (pendingSwitchTarget !== null && pendingSwitchTarget === location.pathname) {
      setPendingSwitchTarget(null);
    }
  }, [location.pathname, pendingSwitchTarget]);

  // ── ⚠️ ALMOST NO LONGER LOAD-BEARING (2026-09-02, steward-app#845) ───────────────────
  // Nearly every workspace-scoped query key now carries the workspace: `workspaceKey('assets',
  // activeWorkspaceId)` → `['assets', <wsId>]`, built through `src/lib/workspaceQueryKey.ts`
  // and enforced by its raw-literal grep test. Two workspaces are two cache entries, so a
  // converted read CANNOT serve the wrong workspace's rows whether or not this reset fires or
  // when. `WorkspaceProvider.keyedQueries.test.tsx` proves exactly that by stubbing
  // `resetQueries` out and switching anyway.
  //
  // steward-app#1185 converted the LAST two exceptions — `advisor-connections`
  // (`advisor/useAdvisorConnections.ts`, which firms can read this family's finances) and
  // `advisor-shared-areas` (`pages/people/WhatYouShare.tsx`, what this family shares with
  // that firm) — so this reset is no longer the only thing standing between one family's
  // advisory-firm badge and another family's. See #1185's escalation comment for what was at
  // stake while it wasn't: `advisor-shared-areas` was keyed `[..., actingFirmId()]` alone,
  // and `actingFirmId()` is null for every family reader, so it was ONE entry across every
  // workspace that user belongs to.
  //
  // Otherwise it is deliberately NOT deleted here: #845 says converting the keys and removing
  // the reset are two changes and must not ride in one review, and #845 §3 is the follow-up
  // that decides whether this stays as defence in depth or goes — #1185 was that decision's
  // last blocker, not the decision itself. What it still buys today is a refetch of mounted
  // observers whose new-workspace entry is cold, and cheap insurance against a future read
  // that forgets its key. What it costs is a refetch of every scoped read on every switch.
  // Weigh those there, not here.
  //
  // Until then: it resets scoped data whenever the workspace pages should use changes from
  // the one they last fetched under — a user switch AND the initial /me resolution
  // correcting an optimistic/fallback workspace. `resetQueries` (not removeQueries) clears
  // AND reliably refetches mounted observers. 'me' is kept so the switcher
  // survives, and 'advisor-firms' alongside it for the same reason: useAdvisorFirm.ts
  // documents that query as workspace-LESS (it reads the caller's own `/me`
  // memberships, not the active workspace), so resetting it on every switch bought
  // nothing but a pointless loading flash across the /advisor nav item and pages —
  // the answer cannot change just because the active workspace did. `prevActive`
  // starts `undefined` (not null) so the very first render doesn't reset — only a
  // real change does.
  //
  // 'advisor-clients' is kept on exactly the same ground, and with more at stake
  // (steward-app#661): it is derived from the caller's own /me memberships too, so a
  // workspace switch cannot change its answer — and clicking "Open" on a client card IS a
  // workspace switch, so resetting it would refire the façade's per-client-workspace
  // fan-out every time an advisor opened a client. That is the cost #661 exists to keep
  // off the hot path; do not let it back in here.
  //
  // LAYOUT effect, not a passive one (steward-app#793). Kept as written, and the reason it
  // was written that way is recorded below — but read the #845 banner above first: the
  // exposure it describes is now closed by the KEYS, not by this timing. While the keys
  // were workspace-agnostic, the window between "children re-render under the new
  // `activeWorkspaceId`" and "the cache is dropped" was a window in which React Query
  // handed those children the PREVIOUS workspace's rows, and since #703/#693/#751 an
  // advisor switching between different client families is routine, which is what made that
  // frame a cross-family disclosure rather than a cosmetic flicker. Keyed reads have no
  // such window at all: there is no shared entry to be stale.
  //
  // WHY THE HOOK IS WHAT FIXES IT, stated carefully, because the obvious explanation is
  // wrong. `resetQueries` notifies observers through React Query's `notifyManager`, whose
  // default scheduler in query-core 5.x is `systemSetTimeoutZero` — a MACROtask, i.e.
  // after paint. A layout effect does not win a race against that timer; it sidesteps it.
  // A click is a discrete event, so React renders, commits, runs layout effects and then
  // flushes that commit's passive effects, all before the handler returns. Running the
  // reset in the LAYOUT phase puts it ahead of the subscribed children's
  // `useSyncExternalStore` passive snapshot check, so that check sees a changed snapshot
  // and forces a synchronous re-render inside the same flush. As a passive effect the
  // reset ran after that check, leaving only the post-paint timer to correct the screen —
  // which is the bug. Measured, not inferred: WorkspaceProvider.switchReset.test.tsx reds
  // on exactly this, and it is a free win on request count too (a query mounting for the
  // first time during the switch now fetches once instead of twice).
  //
  // SCOPE OF THE GUARANTEE, and what #845 did to it. THIS effect's guarantee holds only for
  // a switch originating in a discrete event (WorkspaceSwitcher, the advisor Clients page's
  // "Open", SelectWorkspace). A switch made from an async continuation — NewWorkspaceDialog
  // and FounderBootstrapDialog both `await refresh()` first — still paints one frame before
  // this reset can correct the screen, because there the corrective re-render rides a
  // separate Scheduler macrotask. That was #852. It is closed not by widening this timing
  // but by the keys: on the async path the frame that gets painted now carries the NEW
  // workspace's (cold, therefore empty) entry rather than the previous workspace's rows, so
  // there is nothing stale left in it to disclose. `WorkspaceProvider.keyedQueries.test.tsx`
  // measures that path at the same paint boundary #852 measured it at.
  //
  // Safe here because this SPA has no SSR path (client-only `createRoot` in main.tsx),
  // which is the one situation where `useLayoutEffect` warns. The sibling
  // member-less-redirect effect below deliberately stays passive: it navigates, and
  // nothing about it is a pre-paint concern.
  //
  // ⚠️ **This effect does NOT write storage (steward-app#2623).** It used to copy whatever
  // `activeWorkspaceId` resolved to into the stores, which persisted a FALLBACK: when the
  // remembered id was not a usable membership on some `/me` (a new browser, a firm-branded
  // origin, a momentarily unusable workspace), `resolveActive` picked the first workspace by
  // name and this write made that pick the user's remembered workspace for every later
  // sign-in. Only an explicit act persists now: `switchWorkspace` (which honours
  // `remember: 'tab'` for steward-app#1401's tab-only advisor "Open"), `acceptInvite`,
  // `deleteWorkspace`'s move-off, and `persistActiveWorkspaceId`. A fallback applies for the
  // session (the active id and the request header) and leaves the stored id alone, even when
  // that stored id is genuinely unusable: the next sign-in re-resolves.
  // `WorkspaceProvider.persistence.test.tsx` pins both directions.
  const prevActive = useRef<string | null | undefined>(undefined);
  useLayoutEffect(() => {
    const prev = prevActive.current;
    prevActive.current = activeWorkspaceId;
    if (prev !== undefined && prev !== activeWorkspaceId && activeWorkspaceId) {
      // Both advisor keys come from their own modules rather than being retyped here:
      // a silent typo would start resetting one on every switch, refiring its fetch
      // and flashing the /advisor nav off, with nothing failing to say so (#671 review).
      // `advisor-summary` joins the keep-list for the same reason `advisor-clients` is on
      // it (steward-app#738): both are workspace-LESS reads derived from `/me`, so
      // switching the active workspace cannot change either answer, and dropping them
      // would refire a per-client fan-out on every switch.
      // `advisor-linked-offer` joins for the same reason (steward-app#1364): it answers "which
      // firm and which person does this connect link name", off the published directory, with
      // no workspace on the request — so a switch cannot change its answer either. It is the
      // one key here whose omission was MEASURED rather than reasoned about: `resetQueries`
      // refetches active observers, and the reset below fires on the initial-workspace
      // correction, which is exactly the cold load a deep link is followed from. Without it the
      // route was fetched twice on that path.
      const keep: readonly string[] = [
        'me', ADVISOR_FIRMS_QUERY_KEY[0], ADVISOR_CLIENTS_QUERY_KEY[0], ADVISOR_SUMMARY_QUERY_KEY[0],
        LINKED_ADVISOR_OFFER_QUERY_KEY[0],
      ];
      void queryClient.resetQueries({
        predicate: q => !keep.includes(q.queryKey[0] as string),
      });
    }
  }, [activeWorkspaceId, queryClient]);

  // Route a member-less user (e.g. a fresh invitee, or someone who deleted their
  // last workspace) to the picker so they can accept an invite or create a
  // workspace, instead of dead-ending on an empty Dashboard. Only fires once /me
  // has resolved (a failed/pending /me leaves the app on its default workspace).
  //
  // Note this effect does NOT depend on the pathname: it re-runs when /me resolves
  // or is re-minted, not on navigation. That is why a member-less user can already
  // click into Settings from the picker and stay — and it is also why any write
  // that mints a new ['me'] object (updateProfile, setPreferences) used to yank
  // them back out mid-edit. The allowlist fixes both directions at once.
  useEffect(() => {
    if (!data) return;
    const hasUsable = (data.memberships ?? [])
      .some(m => isUsableMembership(m) && !locallyDeleted.includes(m.workspaceId));
    if (!hasUsable && !isMemberlessAllowedPath(pathnameRef.current)) {
      // No workspace they can enter — could be member-less OR every workspace is
      // suspended. /select-workspace renders both the empty/invite state and the
      // paused-workspace state.
      navigate('/select-workspace', { replace: true });
    }
  }, [data, locallyDeleted, navigate]);

  const switchWorkspace = useCallback((
    workspaceId: string,
    destination?: string,
    opts?: {
      /** Push a new history entry instead of replacing the current one. Default `true`
       *  (replace) keeps every pre-existing caller's behaviour unchanged. `false` is
       *  `AdvisorClients.tsx`'s `openClient` (steward-app#1401): opening a client is a
       *  navigation INTO something, and browser back should be able to leave it — the
       *  same reason a `<Link>` pushes rather than replaces. */
      replace?: boolean;
      /** Which store(s) this pick is written to — see the module header on {@link
       *  writeStored} above. Default `'both'` keeps every pre-existing caller
       *  unchanged. `'tab'` is `openClient`'s: an advisor looking in on a client must
       *  not change what a NEW tab, or the next sign-in, opens into. */
      remember?: 'both' | 'tab';
    },
  ) => {
    // No membership guard here: `resolveActive` validates the pick against the
    // current memberships on the next render (a bogus id just falls back), and a
    // guard would wrongly reject a just-created workspace whose /me refetch hasn't
    // landed in this closure yet.
    setSelectedId(workspaceId); // re-resolves activeWorkspaceId on the next render
    writeStored(workspaceId, opts?.remember === 'tab' ? 'tab' : 'both'); // the explicit pick is what persists (#2623)

    // An explicit destination (the advisor portal's "Open", docs/25 §2/S7) wins
    // outright — the caller knows exactly where switching into this workspace
    // should land, and computing one from the current path would be wrong for a
    // switch that isn't happening FROM a page of content at all.
    //
    // Otherwise: stay on the same page. Only drop a trailing entity-id segment (a
    // detail route like /documents/:id or /portfolio/assets/:id) — that specific
    // entity doesn't exist in the new workspace, but list/section pages are
    // workspace-agnostic and refetch in place. Entity ids are 26-char ULIDs.
    // Exception: the /select-workspace picker isn't a content page — selecting
    // there should drop the user into the workspace (home).
    const path = pathnameRef.current;
    let target: string;
    if (destination !== undefined) {
      target = destination;
    } else if (path === '/select-workspace') {
      target = '/';
    } else {
      const segs = path.split('/').filter(Boolean);
      const isDetailId = segs.length > 0 && /^[0-9a-z]{26}$/i.test(segs[segs.length - 1]!);
      target = isDetailId ? '/' + segs.slice(0, -1).join('/') : path;
    }
    // Called from this same synchronous callback as `setSelectedId` above, alongside it —
    // see `pendingSwitchTarget`'s own comment for the mechanism (steward-app#1409). Its
    // position relative to `navigate()` below is NOT what makes this work (this PR's own
    // security review, correcting an earlier draft of this comment that claimed it was):
    // both this call and `setSelectedId` are ordinary, synchronous `setState`, so they land
    // in the same render regardless of which comes first. What actually matters is that
    // NEITHER is wrapped in `React.startTransition` the way `navigate()` itself is —
    // that is what keeps them out of the lower-priority lane `navigate`'s own location
    // update lands in, and so what makes `switchInFlight` agree with `activeWorkspaceId` on
    // the same render rather than one render later.
    setPendingSwitchTarget(target);
    navigate(target, { replace: opts?.replace ?? true });
  }, [navigate]);

  const refresh = useCallback(async () => {
    const result = await refetch();
    return result.isError ? undefined : result.data;
  }, [refetch]);

  /**
   * The write both accept paths share: POST the token, then refetch `/me` so the new
   * membership is present and the consumed invitation is gone. Returns the workspace the
   * user just joined and does NOTHING about where they are — every navigation and
   * selection decision belongs to the caller (steward-app#1312).
   *
   * Extracted rather than copied because the two callers differ only in that decision. A
   * second literal of the route, its encoding, or the refetch would be a second thing to
   * keep in step, and the half that drifted would be whichever one the next change did not
   * open.
   */
  const postAcceptInvite = useCallback(async (inviteToken: string, options?: AcceptInviteOptions): Promise<string> => {
    // The body is `{}` for every accept except a SPONSORED one, which carries the disclosure copy
    // version the card showed (steward-app#1488) and, for a disclosure that names areas, the two
    // area lists it named (steward-app#1971). The façade refuses a sponsored accept whose version
    // or lists are not what it would show now.
    const body = options?.disclosureVersion
      ? {
        disclosureVersion: options.disclosureVersion,
        ...(options.startAreas && options.lockedAreas
          ? { startAreas: [...options.startAreas], lockedAreas: [...options.lockedAreas] }
          : {}),
      }
      : {};
    const { workspaceId } = await createApiClient(token!)
      .post<{ workspaceId: string }>(`/invites/${encodeURIComponent(inviteToken)}/accept`, body);
    await refetch(); // /me now carries the new membership and drops the consumed invite
    return workspaceId;
  }, [token, refetch]);

  const acceptInvite = useCallback(async (inviteToken: string, options?: AcceptInviteOptions) => {
    const workspaceId = await postAcceptInvite(inviteToken, options);
    setSelectedId(workspaceId);
    writeStored(workspaceId);
    navigate('/', { replace: true });
  }, [postAcceptInvite, navigate]);

  /**
   * Accept an invitation and STAY WHERE YOU ARE (steward-app#1312).
   *
   * ⚠️ **The absence of the three lines above is the whole point of this function**, so do
   * not "tidy" it into a call to {@link acceptInvite}. An advisor accepting a family from
   * the firm's Clients page is not asking to go and look at that family's dashboard: they
   * are working through a list, and switching the session's workspace under them would
   * take the page they are reading away, drop every advisor-scoped query on the floor via
   * the switch effect above, and land them in a household they meant only to admit.
   * `SelectWorkspace` genuinely does mean "take me in", and keeps {@link acceptInvite}.
   *
   * `refetch()` is still awaited inside the shared write, so by the time this resolves
   * `memberships` carries the new client and `invites` no longer carries the accepted row.
   * That is what lets the caller simply re-render rather than track the acceptance itself.
   */
  const acceptInviteInPlace = useCallback(async (inviteToken: string) => {
    await postAcceptInvite(inviteToken);
  }, [postAcceptInvite]);

  const renameWorkspace = useCallback(async (workspaceId: string, name: string) => {
    await createApiClient(token!).put(`/workspaces/${workspaceId}`, { name });
    await refetch();
  }, [token, refetch]);

  /** Hide a workspace from `memberships` immediately, without waiting for the next
   *  `/me` to agree (the membership-mirror cascade this hides is Streams-driven and
   *  async — see `locallyDeleted`'s own comment above). Split out of {@link deleteWorkspace}
   *  (crossmarc/steward-app#1638) so a caller whose OWN write already removed this user's
   *  membership through a different route — a sponsored setup's Cancel deletes the workspace
   *  outright (`SponsoredClientActions.tsx`'s `SponsoredPendingBadge`) — can get the same
   *  immediate-hide without calling `DELETE /workspaces/{id}` a second time, which that
   *  workspace does not belong to this caller to issue in the first place.
   *
   *  ⚠️ **Never call this after a sponsored setup's End** (independent review 2026-09-22, S3;
   *  a prior version of this comment claimed End "removes only the advisor memberships,
   *  including this caller's own" — wrong). appcore's `end()` deletes only the memberships
   *  with `via.kind === 'sponsorship'` for the SETUP's OWN advisors; a caller who presses End
   *  from the roster (a firm admin overseeing it, say) is not guaranteed to be one of those
   *  advisors, or to hold the workspace ONLY that way. Calling this when the caller in fact
   *  still holds the workspace hides a live membership with no way back: `locallyDeleted` is
   *  append-only in this component and nothing else clears an entry out of it, so the
   *  mis-hide would outlive the session. Cancel above has no such ambiguity — appcore deletes
   *  the whole workspace, so every membership on it, including the caller's, is gone either
   *  way. */
  const noteWorkspaceRemoved = useCallback((workspaceId: string) => {
    setLocallyDeleted(prev => prev.includes(workspaceId) ? prev : [...prev, workspaceId]);
  }, []);

  const deleteWorkspace = useCallback(async (workspaceId: string) => {
    await createApiClient(token!).delete(`/workspaces/${workspaceId}`);
    // Hide it immediately (the cascade is async — /me may still return it).
    noteWorkspaceRemoved(workspaceId);

    if (workspaceId === activeWorkspaceId) {
      // Move off the deleted workspace before it disappears from the list. Prefer
      // another active membership (by name); otherwise send the user to the picker.
      // Navigate home (not via switchWorkspace, which keeps the current path) so
      // whatever page triggered the delete unmounts cleanly and the user lands in
      // the fallback workspace's home. That page was /settings and its dialog
      // (`WorkspaceSettings`); this function has no SPA caller while steward-app#1747
      // hides that control for every user.
      const next = memberships
        .filter(m => isUsableMembership(m) && m.workspaceId !== workspaceId)
        .sort((a, b) => a.workspaceName.localeCompare(b.workspaceName))[0];
      if (next) {
        setSelectedId(next.workspaceId); // re-resolves activeWorkspaceId → query reset effect refetches
        writeStored(next.workspaceId);
        navigate('/', { replace: true });
      } else {
        setSelectedId(null);
        writeStored(null); // both stores: no workspace is left at all, unlike sign-out's
        // narrower clearStoredWorkspaceId (sessionStorage only — see that function's own
        // doc, corrected by this branch's own code review), which leaves a legitimate
        // durable pick alone. Here there is no durable pick left to preserve.
        navigate('/select-workspace', { replace: true });
      }
    }
    await refetch();
  }, [token, refetch, activeWorkspaceId, memberships, navigate, noteWorkspaceRemoved]);

  // Memoized rather than a plain `?? []` fallback: it feeds the context-value useMemo
  // below, and an inline `[]` fallback is a fresh array identity every render, which
  // would defeat that memo (and re-render every consumer) on any unrelated re-render.
  const invites = useMemo(() => data?.invites ?? [], [data?.invites]);

  // Fall back to the pre-appcore#346 inference ONLY when the field is absent, so
  // this SPA behaves identically against an older API and correctly against a
  // current one. `?? ` not `||`: an explicit `false` from a current appcore means
  // the grant is spent and must not be second-guessed by counting memberships.
  const canBootstrapTenant = data?.canBootstrapTenant ?? memberships.length === 0;

  const profile = useMemo<UserProfile | null>(
    () => (data ? { userId: data.userId, email: data.email, name: data.name, givenName: data.givenName, preferences: data.preferences } : null),
    [data],
  );

  const updateProfile = useCallback(async (input: { name?: string; givenName?: string }) => {
    await createApiClient(token!).put('/me', input);
    await refetch(); // /me now carries the new name → greeting + account menu update
  }, [token, refetch]);

  // Generic preferences patch — optimistic cache update so consumers react
  // instantly, then a background PUT of just the patch (appcore shallow-merges,
  // so unrelated keys survive); revert to server truth on failure. The named
  // preference writers below are thin wrappers over this.
  const setPreferences = useCallback(async (patch: Partial<UserPreferences>) => {
    const prev = queryClient.getQueryData<MeResponse>(ME_QUERY_KEY);
    if (prev) {
      queryClient.setQueryData<MeResponse>(ME_QUERY_KEY, {
        ...prev,
        preferences: { ...prev.preferences, ...patch },
      });
    }
    try {
      await createApiClient(token!).put('/me', { preferences: patch });
    } catch {
      await refetch(); // revert to server truth on failure (non-critical preference)
    }
  }, [queryClient, token, refetch]);

  // Persist the getting-started checklist dismissal (FR2). The next list is
  // derived from the cached /me so concurrent workspaces don't clobber each other.
  const setFirstRunDismissed = useCallback(async (workspaceId: string, dismissed: boolean) => {
    const prevList = queryClient.getQueryData<MeResponse>(ME_QUERY_KEY)?.preferences?.firstRunDismissed ?? [];
    const nextList = dismissed
      ? Array.from(new Set([...prevList, workspaceId]))
      : prevList.filter(id => id !== workspaceId);
    await setPreferences({ firstRunDismissed: nextList });
  }, [queryClient, setPreferences]);

  // Persist the AI assistant consent acknowledgment (docs/15-ai-consent-privacy.md C2/C3).
  // The family and firm dialogs each have their own pair (steward-app#2523), and `PUT /me`
  // shallow-merges, so writing one pair leaves the other as it was. Like
  // `acceptCredentialConsent` below, deliberately not the optimistic `setPreferences`: a failed
  // PUT must reject so the dialog can say so, and the cached profile moves only on the refetch
  // after a write that succeeded, so an acknowledgment is never shown that was not stored.
  const acknowledgeAiConsent = useCallback(async (scope: AiConsentScope) => {
    const at = new Date().toISOString();
    const preferences = scope === 'firm'
      ? { aiFirmConsentAcknowledgedAt: at, aiFirmConsentVersion: AI_FIRM_CONSENT_VERSION }
      : { aiConsentAcknowledgedAt: at, aiConsentVersion: AI_CONSENT_VERSION };
    await createApiClient(token!).put('/me', { preferences });
    await refetch();
  }, [token, refetch]);

  // Deliberately not `setPreferences`: that path is optimistic and swallows a failed PUT,
  // which for an agreement would let the form open on a record that was never written.
  const acceptCredentialConsent = useCallback(async () => {
    await createApiClient(token!).put('/me', { credentialConsent: { version: CREDENTIAL_CONSENT_VERSION } });
    await refetch();
  }, [token, refetch]);

  const acceptTerms = useCallback(async () => {
    await recordTermsAcceptance(token!);
    await refetch();
  }, [token, refetch]);

  const manageBilling = useCallback(async (workspaceId: string, returnPath: string = '/select-workspace') => {
    const { url } = await createApiClient(token!).post<{ url: string }>('/billing/portal', {
      workspaceId,
      returnUrl: `${window.location.origin}${returnPath}`,
    });
    window.location.assign(url); // hand off to the Stripe-hosted portal
  }, [token]);

  // Single definition of "GET /me has settled" — feeds both the context's
  // meLoaded and BrandSync's meResolved below, so the two can't drift.
  const meLoaded = data !== undefined || isError;

  // ⚠️ **Not the same question as `meLoaded`, and the difference is a live defect surface**
  // (steward-app#1312). `meLoaded` is TRUE for a `/me` that settled with an ERROR, because
  // its consumers only ever needed "stop showing the loading face". A consumer that reads a
  // LIST off `/me` needs the other question: is `[]` the server's answer, or this provider's
  // placeholder? `memberships` and `invites` are both `data?.x ?? []`, so on an error they
  // are empty and `meLoaded` says the payload is in — which is how a page ends up asserting
  // "you have none of these" over a request that never answered.
  //
  // `data !== undefined`, so a REFETCH that failed while a payload from a moment ago is
  // still cached stays `true`: those lists are stale, but they are real server answers, and
  // treating them as unknown would blank a working page on one bad poll.
  const meSucceeded = data !== undefined;

  const value = useMemo<WorkspaceContextValue>(() => ({
    memberships,
    meLoaded,
    meSucceeded,
    profile,
    updateProfile,
    setFirstRunDismissed,
    acknowledgeAiConsent,
    acceptCredentialConsent,
    acceptTerms,
    setPreferences,
    invites,
    canBootstrapTenant,
    activeWorkspaceId,
    activeMembership: memberships.find(m => m.workspaceId === activeWorkspaceId) ?? null,
    switchInFlight,
    switchWorkspace,
    renameWorkspace,
    deleteWorkspace,
    noteWorkspaceRemoved,
    acceptInvite,
    acceptInviteInPlace,
    manageBilling,
    refresh,
  }), [memberships, meLoaded, meSucceeded, profile, updateProfile, setFirstRunDismissed, acknowledgeAiConsent, acceptCredentialConsent, acceptTerms, setPreferences, invites, canBootstrapTenant, activeWorkspaceId, switchInFlight, switchWorkspace, renameWorkspace, deleteWorkspace, noteWorkspaceRemoved, acceptInvite, acceptInviteInPlace, manageBilling, refresh]);

  return (
    <WorkspaceContext.Provider value={value}>
      {/* Push the active tenant's white-label brand (delivered on /me) into the
          app-root BrandProvider — see src/brand/BrandProvider.tsx. */}
      <BrandSync
        branding={data?.branding}
        activeTenantId={memberships.find(m => m.workspaceId === activeWorkspaceId)?.tenantId ?? null}
        meResolved={meLoaded}
      />
      {children}
    </WorkspaceContext.Provider>
  );
}
